import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Filter, Grid, List } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

const Gallery = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Patterns" },
    { id: "traditional", name: "Traditional" },
    { id: "modern", name: "Modern" },
    { id: "graduation", name: "Graduation" },
    { id: "wedding", name: "Wedding" },
    { id: "ceremony", name: "Ceremony" },
  ];

  const patterns = [
    {
      id: 1,
      name: "Adweneasa",
      meaning: "Creativity & Excellence",
      category: "traditional",
      image: "/api/placeholder/300/200",
      description:
        "A traditional pattern symbolizing creativity and excellence in all endeavors.",
    },
    {
      id: 2,
      name: "Ewuraba",
      meaning: "Royalty & Heritage",
      category: "traditional",
      image: "/api/placeholder/300/200",
      description:
        "Represents royal heritage and the importance of preserving cultural traditions.",
    },
    {
      id: 3,
      name: "Fathia",
      meaning: "Unity & Strength",
      category: "modern",
      image: "/api/placeholder/300/200",
      description:
        "Symbolizes unity among people and the strength that comes from working together.",
    },
    {
      id: 4,
      name: "Graduation Special",
      meaning: "Achievement & Success",
      category: "graduation",
      image: "/api/placeholder/300/200",
      description:
        "A special design created to celebrate academic achievements and milestones.",
    },
    {
      id: 5,
      name: "Wedding Harmony",
      meaning: "Love & Partnership",
      category: "wedding",
      image: "/api/placeholder/300/200",
      description:
        "Represents the harmony and love between two people joining in marriage.",
    },
    {
      id: 6,
      name: "Ceremony Honor",
      meaning: "Respect & Tradition",
      category: "ceremony",
      image: "/api/placeholder/300/200",
      description:
        "Honors traditional ceremonies and the respect shown to cultural practices.",
    },
  ];

  const filteredPatterns =
    selectedCategory === "all"
      ? patterns
      : patterns.filter((pattern) => pattern.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
              <h1 className="text-xl font-bold">Pattern Gallery</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Category Filter */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={
                    selectedCategory === category.id ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Patterns Grid/List */}
        <div
          className={
            viewMode === "grid"
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-4"
          }
        >
          {filteredPatterns.map((pattern) => (
            <Card
              key={pattern.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
            >
              <CardContent className="p-0">
                <div className={viewMode === "grid" ? "" : "flex"}>
                  <div
                    className={`${
                      viewMode === "grid" ? "aspect-video" : "w-48 h-32"
                    } bg-muted rounded-t-lg flex items-center justify-center`}
                  >
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <div className="w-8 h-8 bg-primary/20 rounded"></div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {pattern.name}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="font-semibold text-lg mb-1">
                      {pattern.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      {pattern.meaning}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      {pattern.description}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1">
                        Use This Pattern
                      </Button>
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredPatterns.length === 0 && (
          <Card>
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                <Filter className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No patterns found
                </h3>
                <p className="text-muted-foreground mb-4">
                  Try selecting a different category or clear your filters
                </p>
                <Button onClick={() => setSelectedCategory("all")}>
                  Show All Patterns
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Gallery;
